import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import MultichoixSteps from './components/MultichoixSteps';
import TreatmentResults from './components/TreatmentResults';
import MultichoixErrorBoundary from './components/multichoix/MultichoixErrorBoundary';
import { useMultichoixURLState, MULTICHOIX_VIEWS } from './hooks/useMultichoixURLState';
import { useMultichoixEnrichedData } from './hooks/useMultichoixEnrichedData';
import useExtractionExport from './hooks/useExtractionExport';
import { multichoixService } from './services/multichoixService';
import { updateDynamicBreadcrumb } from '@client/utils/page_header';
import { usePageMeta } from '@client/hooks/usePageMeta';

const updateBreadcrumb = (isResultsView) => {
  updateDynamicBreadcrumb(isResultsView ? 'Résultats de recherche' : '');
};

const TreatmentMultichoixSearch = () => {
  const { t } = useTranslation();
  const [searchData, setSearchData] = useState(null);

  // Use the new URL state hook (stores only IDs)
  const {
    urlState,
    currentView,
    mode,
    selections,
    updateField,
    buildResultsURL,
    goToSteps,
    hasValidSearchParams,
  } = useMultichoixURLState();

  const searchMode = mode === 'extraction' ? 'extraction' : 'multichoix';

  // SP-430 E105: extraction generates an Excel file directly (async job +
  // download in a new tab), with NO intermediate HTML results page.
  const { isExporting, startExport } = useExtractionExport();

  // SP-430 E104: in extraction mode the search is named « Extraction de données
  // (Excel) », not « Recherche multichoix ».
  const isExtraction = searchMode === 'extraction';
  usePageMeta({
    title: isExtraction
      ? t('seo|treatment.extractionPageTitle', 'Extraction de données (Excel) - SAgE pesticides')
      : t('seo|treatment.multichoixPageTitle'),
    description: isExtraction
      ? t('seo|treatment.extractionDescription', 'Extraire les traitements phytosanitaires par critères multiples vers un fichier Excel.')
      : t('seo|treatment.multichoixDescription'),
    breadcrumbTitle: isExtraction
      ? t('breadcrumb|treatment.extractionTitle', 'Extraction de données (Excel)')
      : t('breadcrumb|treatment.multichoixTitle'),
  });

  // Callback to sync selection changes to URL
  const handleSelectionChange = useCallback(
    (field, values) => {
      updateField(field, values);
    },
    [updateField]
  );

  // Use the enriched data hook (fetches labels for cold start)
  const {
    enrichedData,
    isLoadingLabels,
    buildSelectionLabels,
    updateEnrichedField,
  } = useMultichoixEnrichedData(urlState);

  // Handle search submission from MultichoixSteps
  const handleSearch = useCallback(
    (data) => {
      // Merge labels: use enriched data (from bulk-initialize) as base,
      // override with step management labels where available.
      // This ensures labels are present whether coming from cold start or interactive use.
      const enrichedLabels = buildSelectionLabels();
      const mergedLabels = { ...enrichedLabels };
      if (data.selectionLabels) {
        Object.entries(data.selectionLabels).forEach(([field, labels]) => {
          if (labels && labels.length > 0) {
            mergedLabels[field] = labels;
          }
        });
      }

      const searchDataWithLabels = {
        selections: data.selections,
        selectionLabels: mergedLabels,
        completedSteps: data.completedSteps,
      };
      setSearchData(searchDataWithLabels);

      // Also update enriched data with step labels for cold-start recovery on refresh
      if (data.selectionLabels) {
        Object.entries(data.selectionLabels).forEach(([field, labels]) => {
          if (labels && labels.length > 0) {
            updateEnrichedField(field, labels);
          }
        });
      }

      // SP-430 E11/E12/E105: extraction generates the Excel file directly — no
      // intermediate HTML results page. The export runs as an async job
      // (useExtractionExport polls it and opens the file in a NEW tab when
      // ready). Multichoix opens its HTML results in a NEW tab; the current tab
      // stays on the criteria.
      if (searchMode === 'extraction') {
        const exportFilters = multichoixService.transformSelectionsToFilters(data.selections);
        startExport(exportFilters, searchDataWithLabels);
      } else {
        window.open(buildResultsURL(data.selections), '_blank', 'noopener,noreferrer');
      }
    },
    [searchMode, startExport, buildResultsURL, buildSelectionLabels, updateEnrichedField]
  );

  // Handle back to steps navigation
  const handleBackToSteps = useCallback(() => {
    goToSteps();
  }, [goToSteps]);

  // Cold start effect: reconstruct searchData from URL + enriched labels when in results view
  useEffect(() => {
    const isResultsView = currentView === MULTICHOIX_VIEWS.RESULTS;
    const hasData = Boolean(searchData);

    if (isResultsView && !hasData && !isLoadingLabels) {
      if (hasValidSearchParams()) {
        const selectionLabels = buildSelectionLabels();

        // Check if labels are actually populated before setting searchData
        // This prevents setting searchData with empty labels while API is still fetching
        const hasLabels = Object.keys(selectionLabels).length > 0;
        const hasSelections = Object.keys(selections).some(
          (key) => selections[key]?.length > 0
        );

        // Only set searchData if we have labels, or if there are no selections (nothing to fetch)
        if (hasLabels || !hasSelections) {
          const completedSteps = Object.keys(selections).filter(
            (key) => selections[key]?.length > 0
          ).length;
          setSearchData({
            selections,
            selectionLabels,
            completedSteps,
          });
        }
        // If hasSelections but !hasLabels, wait for labels to be fetched
      } else {
        goToSteps();
      }
    }
  }, [
    currentView,
    searchData,
    isLoadingLabels,
    hasValidSearchParams,
    selections,
    buildSelectionLabels,
    goToSteps,
  ]);

  // Update breadcrumb when view changes
  useEffect(() => {
    updateBreadcrumb(currentView === MULTICHOIX_VIEWS.RESULTS);
  }, [currentView]);

  // Build initialSearchData from URL + enriched data for MultichoixSteps
  const initialSearchData = useMemo(() => {
    const hasSelections = Object.keys(selections).some(
      (key) => selections[key]?.length > 0
    );

    if (!hasSelections) {
      return null;
    }

    return {
      selections,
      selectionLabels: buildSelectionLabels(),
      completedSteps: Object.keys(selections).filter(
        (key) => selections[key]?.length > 0
      ).length,
    };
  }, [selections, buildSelectionLabels]);

  return (
    <MultichoixErrorBoundary onReset={handleBackToSteps}>
      <Spin
        spinning={isLoadingLabels || isExporting}
        tip={isExporting
          ? t('treatment-search|extraction.export_in_progress', 'Génération du fichier Excel...')
          : t('loading_data', 'Chargement des données...')}
        size="large"
      >
        {currentView === MULTICHOIX_VIEWS.RESULTS && searchData ? (
          <TreatmentResults searchData={searchData} onBackToSteps={handleBackToSteps} searchMode={searchMode} />
        ) : (
          <MultichoixSteps
            onSearch={handleSearch}
            onSelectionChange={handleSelectionChange}
            initialSearchData={initialSearchData}
            enrichedData={enrichedData}
            mode={mode}
          />
        )}
      </Spin>
    </MultichoixErrorBoundary>
  );
};

export default TreatmentMultichoixSearch;
